import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { ChartDataResponse } from "../ceo-dashboard.service";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface DashboardChartProps {
  chartData: ChartDataResponse | null;
  loading: boolean;
  transactionType: string;
  dateRange?: [any, any]; // Optional for backward compatibility
}

const DashboardChart: React.FC<DashboardChartProps> = ({
  chartData,
  loading,
  transactionType,
  dateRange,
}) => {
  const [highlightedType, setHighlightedType] = useState<string | null>(null);
  // Generate all months in the selected range
  const generateMonthsRange = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      // Fallback to months from data if no date range provided
      const months = new Set<string>();
      chartData?.data?.forEach((item) => {
        months.add(item.month_name);
      });
      return Array.from(months).sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      );
    }

    const [startDate, endDate] = dateRange;
    const months: string[] = [];
    let current = dayjs(startDate);

    while (current.isSameOrBefore(dayjs(endDate), "month")) {
      months.push(current.format("MMM YYYY"));
      current = current.add(1, "month");
    }

    return months;
  };

  // Process chart data
  const processChartData = () => {
    if (!chartData?.data) return null;

    const allMonths = generateMonthsRange();
    const groupedData: { [key: string]: { [key: string]: number } } = {};

    // Initialize all months with zero values for all transaction types
    const transactionTypesList = [
      "Deposit",
      "Withdrawal",
      "Subscription",
      "Redemption",
    ];
    allMonths.forEach((month) => {
      groupedData[month] = {};
      transactionTypesList.forEach((type) => {
        groupedData[month][type] = 0;
      });
    });

    // Process actual data
    chartData.data.forEach((item) => {
      const apiMonth = item.month_name; // Format: "August 2025"
      const amount = parseFloat(item.amount_kwd);

      // Convert API month format to chart format for matching
      const chartMonth = dayjs(apiMonth, "MMMM YYYY").format("MMM YYYY"); // "Aug 2025"

      if (allMonths.includes(chartMonth)) {
        groupedData[chartMonth][item.trx_type] = amount;
      }
    });

    const sortedMonths = allMonths;

    const transactionTypes = [
      { type: "Deposit", color: "#7086FD", borderColor: "#7086FD" },
      { type: "Withdrawal", color: "#6FD195", borderColor: "#6FD195" },
      { type: "Subscription", color: "#FFAE4C", borderColor: "#FFAE4C" },
      { type: "Redemption", color: "#07DBFA", borderColor: "#07DBFA" },
    ];

    const typesToShow =
      transactionType && transactionType !== ""
        ? transactionTypes.filter((t) => t.type === transactionType)
        : transactionTypes;

    const datasets = typesToShow.map(({ type, color, borderColor }) => {
      const isHighlighted = highlightedType === null || highlightedType === type;
      return {
        label: type,
        data: sortedMonths.map((month) => groupedData[month]?.[type] || 0),
        borderColor: isHighlighted ? borderColor : borderColor + "30",
        backgroundColor: isHighlighted ? color + "25" : color + "08",
        borderWidth: highlightedType === type ? 5 : isHighlighted ? 3 : 1,
        pointBackgroundColor: isHighlighted ? borderColor : borderColor + "30",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: isHighlighted ? 6 : 3,
        pointHoverRadius: 8,
        tension: 0.4,
        fill: false,
      };
    });

    return {
      labels: sortedMonths,
      datasets,
    };
  };

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: "bottom" as const,
        align: "center" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          font: {
            family: "Montserrat",
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${parseFloat(
              context.parsed.y.toString()
            ).toLocaleString()} KWD`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            family: "Montserrat",
            size: 11,
          },
          color: "#6b7280",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            family: "Montserrat",
            size: 11,
          },
          color: "#6b7280",
          callback: function (value) {
            return parseFloat(value.toString()).toLocaleString() + "K";
          },
        },
      },
    },
  };

  const processedChartData = processChartData();

  return (
    <div className="w-full lg:flex-1 inline-flex flex-col justify-start items-start shadow-md bg-white p-5 rounded-lg gap-5 lg:h-[420px] min-h-[300px]">
      <div className="self-stretch inline-flex justify-between items-center">
        <div className="justify-start text-zinc-800 text-xs font-semibold font-['Montserrat']">
          Monthly transaction amounts by type
        </div>
      </div>
      <div className="self-stretch flex-1 bg-white flex flex-col justify-start items-start gap-2.5">
        <div className="self-stretch flex-1 relative min-h-[300px]">
          {processedChartData ? (
            <Line data={processedChartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {loading ? "Loading chart..." : "No data available"}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-5">
        {[
          { type: "Deposit", icon: "/images/deposit-node.svg" },
          { type: "Withdrawal", icon: "/images/withdraw-node.svg" },
          { type: "Subscription", icon: "/images/subscribtion-node.svg" },
          { type: "Redemption", icon: "/images/redemption-node.svg" },
        ].map(({ type, icon }) => (
          <div
            key={type}
            className="flex items-center gap-1 cursor-pointer select-none"
            style={{ opacity: highlightedType && highlightedType !== type ? 0.4 : 1 }}
            onClick={() => setHighlightedType(highlightedType === type ? null : type)}
          >
            <img src={icon} alt={type} />
            <span className={`justify-start text-xs font-['Inter'] ${highlightedType === type ? "font-semibold text-black" : "font-normal text-black/70"}`}>
              {type}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardChart;
